// Building-block helpers for types that implement inter-object references.
//
// An observer RObject can hold versioned references to other RObjects and
// advance those references through ref-advance operations in its own DAG.
// This module provides the canonical payload shape, metadata tagging, DAG query
// utilities, and monotonicity validation for that mechanism. These are thin,
// generic helpers; types still own authorization checks, barrier semantics, and
// view-time reference resolution.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { position } from "@hyper-hyper-space/hhs3_dag";
import { json } from "@hyper-hyper-space/hhs3_json";
import { version } from "./mvt.js";
export const MAX_REF_ID_LENGTH = 256;
// json.Format for validating the ref-advance portion of a payload.
// Designed for non-strict checking so types can add extra fields.
export const refAdvanceFormat = {
    action: [json.Type.Constant, 'ref-advance'],
    refId: [json.Type.BoundedString, MAX_REF_ID_LENGTH],
    refVersion: json.Type.Something,
};
// Type guard: returns true if the payload is a ref-advance operation.
export function isRefAdvancePayload(payload) {
    return typeof payload === 'object'
        && !Array.isArray(payload)
        && payload['action'] === 'ref-advance';
}
// Construct a ref-advance payload for the given reference and target version.
export function createRefAdvancePayload(refId, refVersion) {
    return {
        action: 'ref-advance',
        refId,
        refVersion: json.toSet(refVersion),
    };
}
// Extract the target version from a ref-advance payload.
export function extractRefVersion(payload) {
    return new Set(json.fromSet(payload.refVersion));
}
// Metadata for appending a ref-advance entry. Tags `ref` for indexed queries and,
// by default, `barrier` for BFT revision. Pass `{ barrier: false }` for a non-barrier ref-advance.
export function createRefAdvanceMeta(refId, opts) {
    var _a;
    const meta = { ref: json.toSet([refId]) };
    if ((_a = opts === null || opts === void 0 ? void 0 : opts.barrier) !== null && _a !== void 0 ? _a : true) {
        meta.barrier = json.toSet(['t']);
    }
    return meta;
}
// Payload and append meta for a barrier ref-advance.
export function prepareRefAdvance(refId, refVersion) {
    return {
        payload: createRefAdvancePayload(refId, refVersion),
        meta: createRefAdvanceMeta(refId),
    };
}
// Find all ref-advance entries for a given refId up to a DAG position.
export function findRefAdvances(dag, refId, at) {
    return __awaiter(this, void 0, void 0, function* () {
        return dag.findCoverWithFilter(at, { containsValues: { ref: [refId] } });
    });
}
// Find ref-advance barrier entries for a given refId that are concurrent
// to `at` when observed from `from`. Used for (at, from) revision semantics:
// barriers in this set may retroactively affect how concurrent operations
// are interpreted.
export function findConcurrentRefAdvanceBarriers(dag, refId, at, from) {
    return __awaiter(this, void 0, void 0, function* () {
        return dag.findConcurrentCoverWithFilter(from, at, { containsValues: { ref: [refId], barrier: ['t'] } });
    });
}
// Resolve referenced version(s) at `at` in the observer DAG, optionally widened
// by ref-advance barriers concurrent to `at` when observed from `from`.
// Observers pass the result as the referenced object's `at`; the referenced
// object's `from` is typically a separate resolution at the view frontier
// (call with from === at to get ref-advances in the frontier's history only,
// without concurrent barrier widening).
//
// When from === at, only ref-advances in the history of `at` contribute.
// When from !== at, concurrent ref-advance barriers widen the result,
// implementing the BFT revision mechanism in the observer DAG.
//
// `isLive`, when supplied, is consulted per ref-advance entry (in BOTH folds):
// an entry for which it returns false is SKIPPED, so a voided ref-advance (e.g.
// an observation rejected at-use by its authorization gate) contributes no
// version. Omitting `isLive` is the geometric resolution (every ref-advance
// counts) and is the default everywhere a gate does not apply.
export function resolveRefVersionAtPosition(dag, refId, at, from, isLive) {
    return __awaiter(this, void 0, void 0, function* () {
        const causal = yield findRefAdvances(dag, refId, at);
        const concurrent = yield findConcurrentRefAdvanceBarriers(dag, refId, at, from);
        const result = version();
        for (const hash of causal) {
            if (isLive !== undefined && !(yield isLive(hash)))
                continue;
            const entry = yield dag.loadEntry(hash);
            if (entry === undefined)
                continue;
            if (isRefAdvancePayload(entry.payload)) {
                for (const h of extractRefVersion(entry.payload))
                    result.add(h);
            }
        }
        for (const hash of concurrent) {
            if (causal.has(hash))
                continue;
            if (isLive !== undefined && !(yield isLive(hash)))
                continue;
            const entry = yield dag.loadEntry(hash);
            if (entry === undefined)
                continue;
            if (isRefAdvancePayload(entry.payload)) {
                for (const h of extractRefVersion(entry.payload))
                    result.add(h);
            }
        }
        return result.size > 0 ? result : version(refId);
    });
}
// Resolve referenced-object versions for checking an observer entry against a foreign
// object. `entryHash` and `observerFrom` are positions in the observer DAG; the returned
// `refAt` / `refFrom` are versions in the referenced object's DAG (suitable as args to
// `referenced.getView(refAt, refFrom)`).
export function resolveRefVersions(observerDag, refId, entryHash, observerFrom) {
    return __awaiter(this, void 0, void 0, function* () {
        const refAt = yield resolveRefVersionAtPosition(observerDag, refId, version(entryHash), observerFrom);
        const refFrom = yield resolveRefVersionAtPosition(observerDag, refId, observerFrom, observerFrom);
        return { refAt, refFrom };
    });
}
// Returns true iff `newer` is at or above `older` in the referenced object's DAG
// (older is in newer's causal past, or they denote the same position). Closed on both
// ends: equal positions pass. This is not negated by refVersionAtOrBelow; concurrent
// positions fail both comparisons.
export function refVersionAtOrAbove(referencedDag, newer, older) {
    return __awaiter(this, void 0, void 0, function* () {
        const fork = yield referencedDag.findForkPosition(older, newer);
        return fork.forkA.size === 0;
    });
}
// Converse of refVersionAtOrAbove: true iff `v` is at or below `ceiling` in the
// referenced object's DAG. Use for ref-advance stability (e.g. projectForeignBound);
// do not implement as !refVersionAtOrAbove(...).
export function refVersionAtOrBelow(referencedDag, v, ceiling) {
    return __awaiter(this, void 0, void 0, function* () {
        return refVersionAtOrAbove(referencedDag, ceiling, v);
    });
}
// Project a foreign revision bound into the observer DAG: find the earliest unstable
// ref-advance(s) at or below `localAt`. A ref-advance is stable iff its referenced
// version is at or below `foreignRevisionBound`; unstable otherwise.
//
// Starting from the ref-advance cover at `localAt`, descend through unstable ref-advances
// via their preds; a branch settles when no unstable ref-advance sits below it. The
// referenced object's create op is an implicit stable ref-advance to version(refId)
// (always at or below `foreignRevisionBound`), so an empty below-cover settles the
// branch. Below the returned bound the referenced version is at or below
// `foreignRevisionBound`. If no ref-advance is unstable, return `localAt`.
//
// Assumes monotonic ref-advances: a stable ref-advance has only stable ref-advances
// below it, so the descent can stop at the first stable ref-advance on each branch.
export function projectForeignBound(observerDag, refId, referencedDag, localAt, foreignRevisionBound) {
    return __awaiter(this, void 0, void 0, function* () {
        const refFilter = { containsValues: { ref: [refId] } };
        const bound = version();
        const visited = new Set();
        const stabilityCache = new Map();
        const isStable = (hash) => __awaiter(this, void 0, void 0, function* () {
            const cached = stabilityCache.get(hash);
            if (cached !== undefined)
                return cached;
            const entry = yield observerDag.loadEntry(hash);
            const refVersion = extractRefVersion(entry.payload);
            const result = yield refVersionAtOrBelow(referencedDag, refVersion, foreignRevisionBound);
            stabilityCache.set(hash, result);
            return result;
        });
        const queue = [...(yield observerDag.findCoverWithFilter(localAt, refFilter))];
        while (queue.length > 0) {
            const r = queue.shift();
            if (visited.has(r))
                continue;
            visited.add(r);
            if (yield isStable(r))
                continue;
            const entry = yield observerDag.loadEntry(r);
            const preds = position(...json.fromSet(entry.header.prevEntryHashes));
            const below = yield observerDag.findCoverWithFilter(preds, refFilter);
            const unstableBelow = [];
            for (const b of below) {
                if (!(yield isStable(b)))
                    unstableBelow.push(b);
            }
            if (unstableBelow.length === 0) {
                bound.add(r);
            }
            else {
                queue.push(...unstableBelow);
            }
        }
        return bound.size > 0 ? bound : localAt;
    });
}
// Validates that a proposed ref-advance is monotonic at insertion time. For each
// predecessor in `at`, resolves the current reference in the observer DAG (causal
// only, from === at) and requires `newRefVersion` to be at or above that version
// in the referenced object's DAG. On merge, every branch predecessor must pass.
export function validateRefAdvanceMonotonicity(observerDag, referencedDag, refId, newRefVersion, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (at.size === 0) {
            const current = yield resolveRefVersionAtPosition(observerDag, refId, at, at);
            return refVersionAtOrAbove(referencedDag, newRefVersion, current);
        }
        for (const pred of at) {
            const current = yield resolveRefVersionAtPosition(observerDag, refId, version(pred), version(pred));
            if (!(yield refVersionAtOrAbove(referencedDag, newRefVersion, current))) {
                return false;
            }
        }
        return true;
    });
}
